import React from "react";
import { toast } from "react-toastify";
import AdminNotificationToast from "../components/Notifications/AdminNotificationToast";
import { playNotificationSound } from "./playNotificationSound";
import "../assets/custom css/adminNotificationToast.scss";

const NOTIFICATION_ICON = "/favicon.png";
const DEDUP_WINDOW_MS = 20000;
const recentNotifications = new Map();

const navigateTo = (redirectTo) => {
  if (!redirectTo) return;
  window.location.href = redirectTo.startsWith("http")
    ? redirectTo
    : `${window.location.origin}${redirectTo}`;
};

const getNotificationKey = (payload = {}) => {
  const id = payload._id || payload.notificationId || payload?.data?.notificationId;
  if (id) return String(id);
  const title = payload.title || "";
  const body = payload.description || payload.body || "";
  return `${title}::${body}`;
};

const shouldShowNotification = (key) => {
  const now = Date.now();
  const lastShown = recentNotifications.get(key);
  if (lastShown && now - lastShown < DEDUP_WINDOW_MS) {
    return false;
  }
  recentNotifications.set(key, now);
  for (const [storedKey, timestamp] of recentNotifications.entries()) {
    if (now - timestamp > DEDUP_WINDOW_MS) {
      recentNotifications.delete(storedKey);
    }
  }
  return true;
};

export const showBrowserNotification = (payload = {}) => {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return null;
  }

  const title = payload.title || "vSARA Admin";
  const body = payload.description || payload.body || "You have a new notification.";
  const redirectTo = payload.redirectTo || payload?.data?.redirectTo;

  try {
    const notification = new Notification(title, {
      body,
      icon: NOTIFICATION_ICON,
      badge: NOTIFICATION_ICON,
      silent: false,
      tag: getNotificationKey(payload),
      data: { redirectTo },
    });

    notification.onclick = () => {
      window.focus();
      navigateTo(redirectTo);
      notification.close();
    };

    return notification;
  } catch (error) {
    console.error("[Notification] Failed to show browser notification:", error);
    return null;
  }
};

export const showInAppNotification = (payload = {}) => {
  const title = payload.title || "vSARA Admin";
  const body = payload.description || payload.body || "You have a new notification.";
  const redirectTo = payload.redirectTo || payload?.data?.redirectTo;
  const toastId = getNotificationKey(payload);

  playNotificationSound();

  toast(
    ({ closeToast }) => (
      <AdminNotificationToast
        title={title}
        body={body}
        onClose={closeToast}
        onView={
          redirectTo
            ? () => {
                closeToast();
                navigateTo(redirectTo);
              }
            : null
        }
      />
    ),
    {
      toastId,
      className: "vsara-toast-item",
      progressClassName: "vsara-toast-progress",
      icon: false,
      autoClose: 7000,
      closeButton: false,
      hideProgressBar: false,
    }
  );
};

const showOSNotification = async (payload = {}) => {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }

  const title = payload.title || "vSARA Admin";
  const body = payload.description || payload.body || "You have a new notification.";
  const redirectTo = payload.redirectTo || payload?.data?.redirectTo;
  const tag = getNotificationKey(payload);

  const options = {
    body,
    icon: NOTIFICATION_ICON,
    badge: NOTIFICATION_ICON,
    tag,
    renotify: false,
    silent: false,
    vibrate: [120, 60, 120],
    data: { redirectTo, click_action: redirectTo },
  };

  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.ready;
      if (registration?.showNotification) {
        await registration.showNotification(title, options);
        return;
      }
    }
    showBrowserNotification({ ...payload, title, description: body, redirectTo });
  } catch (error) {
    console.error("[Notification] showOSNotification failed:", error);
  }
};

export const showAdminAlert = async (payload = {}) => {
  const key = getNotificationKey(payload);
  if (!shouldShowNotification(key)) {
    return;
  }

  const isTabVisible = typeof document !== "undefined" && document.visibilityState === "visible";

  if (isTabVisible) {
    showInAppNotification(payload);
    return;
  }

  playNotificationSound();
  await showOSNotification(payload);
};

export const ensureNotificationPermission = async () => {
  if (typeof Notification === "undefined") {
    return "unsupported";
  }
  if (Notification.permission === "granted") {
    return "granted";
  }
  if (Notification.permission === "denied") {
    return "denied";
  }
  return Notification.requestPermission();
};
